import { FC, useEffect } from 'react';
import { z } from 'zod';
import { IntlShape, useIntl } from 'react-intl';

import { graphql } from '../gql';
import { useApiQuery } from '../query/hooks';
import { useModalContext } from '../modal/ModalContext';
import { CircularProgress } from '@mui/material';
import { Stack } from '@mui/system';
import { ControlledFormField } from '../form/ControlledFormField';
import { SelectJourneyTeam } from '../SelectJourneyTeam/SelectJourneyTeam';
import { FormErrors } from '../form/FormErrors';
import { FormProvider } from 'react-hook-form';
import { useUpdateJourney } from './list/useUpdateJourney';
import { ModalContent } from '../modal/components/ModalContent';
import { ModalActions } from '../modal/components/ModalActions';
import { useTypedForm } from '../../../shared/fe/hooks/useForm';

const editJourney_SingleJourneyQuery = graphql(`
	query editJourney_SingleJourneyQuery($id: UUID!) {
		getJourney(by: { id: $id }) {
			name
			teams {
				id
				name
			}
		}
	}
`);

const editFormSchema = (intl: IntlShape) =>
	z.object({
		jourenyTeamIds: z.array(z.string()).nullable(),
	});

type FormValues = z.infer<ReturnType<typeof editFormSchema>>;

const editJourneyModalSchema = z.object({
	id: z.string(),
	name: z.string(),
});

type Props = z.infer<typeof editJourneyModalSchema>;

const EditJourneyModal: FC<Props> = ({ id, name }) => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const { journeyUpdate } = useUpdateJourney();

	const form = useTypedForm({
		schema: editFormSchema(intl),
	});

	const { data: queryData, isLoading: isJourneyLoading } = useApiQuery(editJourney_SingleJourneyQuery, {
		variables: {
			id,
		},
	});

	// sync form initial values
	useEffect(() => {
		form.reset({
			jourenyTeamIds: queryData?.getJourney?.teams.map((team) => team.id) ?? [],
		});
	}, [form, form.reset, queryData?.getJourney?.name, queryData?.getJourney?.teams]);

	const onSubmit = async ({ jourenyTeamIds }: FormValues) => {
		const teamsBeforeUpdateIds = queryData?.getJourney?.teams.map((team) => team.id);
		const updatedTeamIds = jourenyTeamIds;
		const removedIDs = teamsBeforeUpdateIds?.filter((id) => !updatedTeamIds?.includes(id));
		const addedIDs = updatedTeamIds?.filter((id) => !teamsBeforeUpdateIds?.includes(id));

		if (removedIDs != null && addedIDs != null) {
			if (removedIDs.length > 0) {
				await journeyUpdate(
					{ journeyId: id, journeyName: name, teams: removedIDs.map((id) => ({ disconnect: { id: id } })) },
					{
						onSuccess: () => {
							closeModal();
						},
					},
				);
			}
			if (addedIDs.length > 0) {
				await journeyUpdate(
					{ journeyId: id, journeyName: name, teams: addedIDs.map((id) => ({ connect: { id: id } })) },
					{
						onSuccess: () => {
							closeModal();
						},
					},
				);
				return;
			}

			await journeyUpdate(
				{ journeyId: id, journeyName: name },
				{
					onSuccess: () => {
						closeModal();
					},
				},
			);
		}
	};

	return (
		<FormProvider {...form}>
			<form noValidate onSubmit={form.handleSubmit(onSubmit)}>
				<ModalContent title={intl.formatMessage({ id: 'form.journey.title.edit' })}>
					{isJourneyLoading ? (
						<Stack p={3} alignItems={'center'}>
							<CircularProgress />
						</Stack>
					) : (
						<>
							<ControlledFormField
								form={form}
								name={'jourenyTeamIds'}
								label={intl.formatMessage({ id: 'form.select-team-input.label' })}
								render={(props) => (
									<SelectJourneyTeam teamIds={queryData?.getJourney?.teams.map((t) => t.id) ?? []} {...props} value={props.value || []} />
								)}
							/>
							<FormErrors form={form} />
						</>
					)}
				</ModalContent>
				<ModalActions
					handleCancelButtonClick={closeModal}
					handlePrimaryButtonClick={form.handleSubmit(onSubmit)}
					cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
					primaryButtonText={intl.formatMessage({ id: 'form.journey.button.update' })}
				/>
			</form>
		</FormProvider>
	);
};

export { EditJourneyModal, editJourneyModalSchema };
